import { type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md';

interface LinkButtonProps extends Omit<LinkProps, 'children'> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  children: ReactNode;
}

const variants: Record<Variant, string> = {
  primary: 'bg-primary text-primary-fg hover:bg-primary-hover',
  secondary:
    'bg-surface text-text border border-border hover:bg-surface-elevated hover:border-primary',
  ghost: 'bg-transparent text-text-muted hover:text-text hover:bg-surface',
};

const sizes: Record<Size, string> = {
  sm: 'h-10 px-4 text-sm gap-2',
  md: 'h-12 px-5 text-[0.95rem] gap-2',
};

export function LinkButton({
  variant = 'primary',
  size = 'md',
  fullWidth,
  className,
  leadingIcon,
  trailingIcon,
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link
      {...rest}
      className={cn(
        'inline-flex items-center justify-center rounded-xl font-medium font-sans no-underline',
        'transition-[background-color,color,border-color,transform] duration-150 ease-out active:scale-[0.98]',
        'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {leadingIcon}
      <span>{children}</span>
      {trailingIcon}
    </Link>
  );
}
